import {
  socialPostSceneBlockV1Schema,
  socialPostSceneShapeBlockV1Schema,
  type SocialPostSceneBlockV1,
  type SocialPostSceneCanvasV1,
  type SocialPostSceneShapeBlockV1,
  type SocialPostShapeType,
} from "@runcheetah/shared";

export type CreativeLayerCreateKind = "text" | "shape" | "rect" | "frame" | "logo";

export function createDefaultCreativeLayerBlock(input: {
  kind: CreativeLayerCreateKind;
  id: string;
  canvas: SocialPostSceneCanvasV1;
  zIndex: number;
  fontFamily?: string;
  shapeType?: SocialPostShapeType;
  logoObjectKey?: string | null;
}): SocialPostSceneBlockV1 {
  const width = Math.round(input.canvas.width * 0.44);
  const height = Math.round(input.canvas.height * 0.12);
  const x = Math.round(input.canvas.width / 2 - width / 2);
  const y = Math.round(input.canvas.height / 2 - height / 2);
  const base = {
    id: input.id,
    x,
    y,
    width,
    height,
    rotation: 0,
    opacity: 1,
    zIndex: input.zIndex,
    visible: true,
  };

  if (input.kind === "text") {
    return {
      ...base,
      type: "text",
      text: "New text",
      fill: "#171717",
      fontFamily: input.fontFamily ?? "Inter",
      fontSize: Math.max(32, Math.round(input.canvas.width * 0.052)),
      fontStyle: "normal",
      align: "center",
      verticalAlign: "middle",
      lineHeight: 1.12,
      letterSpacing: 0,
      padding: 0,
    };
  }

  if (input.kind === "shape") {
    return createCreativeShapeBlock({ ...base, shapeType: input.shapeType ?? "ellipse" });
  }

  if (input.kind === "logo") {
    const logoWidth = Math.round(input.canvas.width * 0.28);
    const logoHeight = Math.round(input.canvas.height * 0.08);
    return socialPostSceneBlockV1Schema.parse({
      ...base,
      type: "logo",
      source: "brand_logo",
      x: Math.round(input.canvas.width / 2 - logoWidth / 2),
      y: Math.round(input.canvas.height / 2 - logoHeight / 2),
      width: logoWidth,
      height: logoHeight,
      assetObjectKey: input.logoObjectKey || undefined,
      fit: "contain",
      cornerRadius: 0,
    });
  }

  if (input.kind === "rect") {
    return {
      ...base,
      type: "rect",
      fill: "rgba(23, 23, 23, 0.08)",
      stroke: "#171717",
      strokeWidth: 0,
      cornerRadius: 24,
    };
  }

  return {
    ...base,
    type: "frame",
    fill: undefined,
    stroke: "#171717",
    strokeWidth: 3,
    cornerRadius: 24,
    insetPadding: 0,
  };
}

export function getNextRootLayerZIndex(blocks: SocialPostSceneBlockV1[]): number {
  return Math.max(-1, ...blocks.map((block) => block.zIndex)) + 1;
}

export function applyCreativeShapePreset(
  block: SocialPostSceneShapeBlockV1,
  shapeType: SocialPostShapeType,
): SocialPostSceneShapeBlockV1 {
  const {
    points: _points,
    closed: _closed,
    tension: _tension,
    sides: _sides,
    radius: _radius,
    innerRadius: _innerRadius,
    outerRadius: _outerRadius,
    angle: _angle,
    clockwise: _clockwise,
    pathData: _pathData,
    ...base
  } = block;

  const presetFields = getShapePresetFields(shapeType, block.width, block.height);

  return socialPostSceneShapeBlockV1Schema.parse({
    ...base,
    shapeType,
    ...presetFields,
    ...(shapeType === "line"
      ? {
          stroke: block.stroke ?? "#171717",
          strokeWidth: Math.max(block.strokeWidth, 3),
        }
      : {}),
  });
}

function createCreativeShapeBlock(input: {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
  zIndex: number;
  visible: boolean;
  shapeType: SocialPostShapeType;
}): SocialPostSceneBlockV1 {
  return socialPostSceneBlockV1Schema.parse({
    ...input,
    type: "shape",
    fill: "#ffffff",
    stroke: "#171717",
    strokeWidth: input.shapeType === "ellipse" ? 0 : 3,
    cornerRadius: 0,
    ...getShapePresetFields(input.shapeType, input.width, input.height),
  });
}

function getShapePresetFields(
  shapeType: SocialPostShapeType,
  width: number,
  height: number,
): Partial<SocialPostSceneShapeBlockV1> {
  const radius = Math.max(1, Math.min(width, height) / 2);

  if (shapeType === "line") {
    return {
      fill: undefined,
      points: [0, height / 2, width, height / 2],
      closed: false,
      tension: 0,
      lineCap: "round",
      lineJoin: "round",
    };
  }

  if (shapeType === "polygon") {
    return { sides: 6, radius };
  }

  if (shapeType === "star") {
    return { sides: 5, innerRadius: radius * 0.45, outerRadius: radius };
  }

  if (shapeType === "ring") {
    return { fill: undefined, innerRadius: radius * 0.55, outerRadius: radius };
  }

  if (shapeType === "arc") {
    return {
      fill: undefined,
      innerRadius: radius * 0.55,
      outerRadius: radius,
      angle: 180,
      clockwise: false,
    };
  }

  if (shapeType === "path") {
    return {
      pathData: `M0 ${height / 2} C${width * 0.25} 0 ${width * 0.75} ${height} ${width} ${height / 2}`,
    };
  }

  return {};
}
